import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
  ChartOptions,
} from "chart.js";
import { HistogramDataType } from "../types";
import ClassificationSlider from "./UserDefinedClassSlider";
import { Height } from "@mui/icons-material";

// Register Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Title);


// Helper function to format numbers with superscript exponents for Chart.js
const formatWithPrettyExponents = (value: string | number, useLogFormat: boolean): string => {
  // Only apply pretty formatting if is_log is true
  if (!useLogFormat) {
    // Standard formatting without exponents
    if (typeof value === 'string' && value.includes(' - ')) {
      const parts = value.split(' - ');
      const formattedParts = parts.map(part => {
        const num = Number(part);
        if (!isNaN(num)) {
          return Math.abs(num) > 1000000 ? num.toExponential(2) : num.toFixed(2);
        }
        return part; // Keep as is if not a number
      });
      return formattedParts.join(" - ");
    }
    
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return Math.abs(num) > 1000000 ? num.toExponential(2) : num.toFixed(2);
  }
  
  // Unicode superscript characters for exponents
  const superscriptMap: {[key: string]: string} = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', 
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
    '-': '⁻', '.': '·'
  };
  
  // Convert a number to superscript string
  const toSuperscript = (num: number): string => {
    const numStr = num.toFixed(2);
    return [...numStr].map(char => superscriptMap[char] || char).join('');
  };
  
  // Check if the label appears to be a simple negative number range (like -2.197 - -1.81)
  // rather than an exponent range
  if (typeof value === 'string' && value.includes(' - ')) {
    const parts = value.split(' - ');
    
    // Check if both parts are numbers
    if (parts.every(part => !isNaN(Number(part)))) {
      const numbers = parts.map(Number);
      
      // Process each part and format with exponents
      const formattedParts = numbers.map(num => {
        return `10${toSuperscript(num)}`;
      });
      
      return formattedParts.join(" - ");
    }
  }
  
  // For single values
  const num = Number(value);
  if (isNaN(num)) return String(value); // Not a number, return as is
  
  return `10${toSuperscript(num)}`;
};




interface BarHistogramProps {
  histogramData: HistogramDataType | null;
  sliderValues: number[];
    setSliderValues: (newValues: number[]) => void;
    isFlipped: boolean;
    setIsFlipped: () => void;
  //categories?: { [key: string]: number } | null; // Optional categories for Discrete
}

const BarHistogram: React.FC<BarHistogramProps> = ({ histogramData, sliderValues, setSliderValues, isFlipped, setIsFlipped }) => {

  if (!histogramData) {
    return <p>No valid data provided.</p>;
  }
  if (histogramData.type.length == 0)
  {
    return <p>No valid data provided.</p>;
  }


  let options : ChartOptions<"bar"> = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: "Data Histogram",
      },
      legend: { display: false },
    },
  }

  const { type } = histogramData;
  /*** ✅ Continuous Histogram ***/
  if (type === "Continuous") {
    const { bins, min, max, binWidth } = histogramData;

    const annotationLines = sliderValues.map((value) => ({
      type: 'line' as const,
      mode: "vertical",
      scaleID: "x",
      value: ((value - min) / (max - min)) * (bins.length - 1), // Convert to bin index
      borderColor: "#1976d2",
      borderWidth: 2,
      label: {
        content: value.toFixed(2), // Show exact value
        enabled: true,
        backgroundColor: "#1976d2",
      },
    }));

    options={
      maintainAspectRatio: false,
      plugins: { 
        annotation: { annotations: annotationLines },
        ...options.plugins
      },
      scales: {
        x: { 
          title: { display: false },
          ticks: { maxRotation: 45, font: { size: 10 } }
        },
        y: { 
          title: { display: false },
          beginAtZero: true,
          ticks: { font: { size: 10 } }
        },
      },
    }
    

    return (
      <div 
        style={{ width: '100%', marginBottom: '15px' }}>

        
        <div style={{ height: '220px', width: '100%' }}>
          <Bar
            style={{height:"200px"}}
            data={{
              labels: bins.map((_, i) =>
                `${formatWithPrettyExponents(min + i * binWidth, false)} - ${formatWithPrettyExponents(min + (i + 1) * binWidth, false)}` /* ${(min + i * binWidth).toFixed(2)} - ${(min + (i + 1) * binWidth).toFixed(2)}` */
              ),
              datasets: [
                {
                  label: "Count",
                  data: bins,
                  backgroundColor: "rgba(75, 192, 192, 0.6)",
                  borderColor: "rgba(75, 192, 192, 1)",
                },
              ],
            }}
            options={options}
          />
        </div>
        
        {/* Classification Slider */}
        <ClassificationSlider 
          histogramData={histogramData}
          sliderValues={sliderValues}
          setSliderValues={setSliderValues}
          isFlipped={isFlipped}
          handleReverse={setIsFlipped}
        />
      </div>
    );
    
  }

  /*** ✅ Discrete Histogram ***/
  if (type === "Discrete") {
    //console.log("Rendering Discrete Bar Chart");
  
    // Extract bins (category -> count mapping)
    const categories = histogramData.categories;
    const counts = histogramData.counts;
  
    return (
      <div style={{ height: '220px', width: '100%' }}>
        <Bar
          style={{height:"170px"}}
          data={{
            labels: categories, // Category labels
            datasets: [
              {
                data: counts,
                backgroundColor: "rgba(75, 192, 192, 0.6)",
                borderColor: "rgba(75, 192, 192, 1)",
              },
            ],
            
          }}
          options={options}
        />
      </div>
    );
  }

  return <p>No valid data provided_3.</p>;
};

export default BarHistogram;
